import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Calendar, Globe, Phone, CreditCard, Smartphone } from 'lucide-react';

export function UpgradeToPremium() {
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'swish' | 'klarna'>('card');
  const [swishNumber, setSwishNumber] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<'premium' | 'standard' | 'day'>('standard');
  
  // Read the plan parameter from the URL when the component mounts
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');
    if (planParam && ['premium', 'standard', 'day'].includes(planParam)) {
      setSelectedPlan(planParam as 'premium' | 'standard' | 'day');
    }
  }, [location.search]);
  const [cardDetails, setCardDetails] = useState({
    number: '',
    name: '',
    expiry: '',
    cvc: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSwishSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate a processing delay
    setTimeout(() => {
      navigate('/payment-confirmation', { state: { swishNumber } });
    }, 1500);
  };

  const handleCardSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate a processing delay
    setTimeout(() => {
      navigate('/payment-confirmation', { state: { cardDetails } });
    }, 1500);
  };

  const features = [
    {
      icon: <Search className="w-6 h-6" />,
      title: "AI-sökning",
      description: "Hitta rätt stiftelse genom att beskriva din situation med AI-assisterad sökning. Ökar dina chanser för att lyckas med dina ansökningar väsentligt. Vår AI analyserar dina behov och matchar dem mot stiftelsernas kriterier."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Ansökningstider",
      description: "Få koll på alla ansökningstider och missa aldrig en ansökningsperiod. Optimera ditt ansökningsarbete med påminnelser och kalenderintegrering. Få notifieringar när viktiga deadlines närmar sig."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "E-post & webbsidor",
      description: "Extrahera information som inte finns tillgängligt i stiftelsedatabasen via smarta webb-baserade AI-lösningar. Få direkt tillgång till kontaktuppgifter och webbadresser för enklare kommunikation."
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Stiftelseaktivitet",
      description: "Se hur aktiva stiftelserna är med utdelningar och få insikt i deras verksamhet för att bättre bedöma dina chanser. Få statistik över tidigare utdelningar och framgångsrika ansökningar."
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "Ansökningsmallar",
      description: "Få tillgång till professionella ansökningsmallar anpassade för olika typer av stiftelser. Spara tid och öka dina chanser genom att använda beprövade formuleringar."
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Prioriterad support",
      description: "Som premiummedlem får du tillgång till prioriterad support med svar inom 24 timmar. Våra experter hjälper dig med specifika frågor om stiftelser och ansökningsprocesser."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F2C1D1] to-white py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Uppgradera till Premium
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Få tillgång till alla avancerade funktioner och maximera dina chanser att hitta rätt stiftelse för dina behov.
          </p>
        </div>

        {/* Top row: Features and Payment Form side by side */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Left Column - Features */}
          <div className="bg-white rounded-xl shadow-lg p-8 h-full">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Vad du får med Premium</h2>
            
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div key={index} className="flex gap-4">
                  <div className="flex-shrink-0 p-2 bg-[#A3C1DA]/20 text-[#A3C1DA] rounded-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Payment Form */}
          <div id="payment-section" className="bg-white rounded-xl shadow-lg p-8 h-full">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">Välj betalningsmetod</h2>
            
            <div className="grid grid-cols-3 gap-2 mb-6">
              <button
                onClick={() => setPaymentMethod('card')}
                className={`p-3 flex flex-col items-center gap-2 text-sm font-medium rounded-lg border ${
                  paymentMethod === 'card'
                    ? 'border-[#A3C1DA] bg-[#A3C1DA]/10 text-[#A3C1DA]'
                    : 'border-gray-200 text-gray-500 hover:border-[#A3C1DA]/50'
                }`}
              >
                <CreditCard size={24} />
                <span>Kreditkort</span>
              </button>
              <button
                onClick={() => setPaymentMethod('swish')}
                className={`p-3 flex flex-col items-center gap-2 text-sm font-medium rounded-lg border ${
                  paymentMethod === 'swish'
                    ? 'border-[#A3C1DA] bg-[#A3C1DA]/10 text-[#A3C1DA]'
                    : 'border-gray-200 text-gray-500 hover:border-[#A3C1DA]/50'
                }`}
              >
                <Smartphone size={24} />
                <span>Swish</span>
              </button>
              <button
                onClick={() => setPaymentMethod('klarna')}
                className={`p-3 flex flex-col items-center gap-2 text-sm font-medium rounded-lg border ${
                  paymentMethod === 'klarna'
                    ? 'border-[#A3C1DA] bg-[#A3C1DA]/10 text-[#A3C1DA]'
                    : 'border-gray-200 text-gray-500 hover:border-[#A3C1DA]/50'
                }`}
              >
                <CreditCard size={24} />
                <span>Klarna</span>
              </button>
            </div>
            
            {paymentMethod === 'card' ? (
              <form onSubmit={handleCardSubmit} className="space-y-4">
                <div>
                  <label htmlFor="cardNumber" className="block text-sm text-gray-600 mb-1">
                    Kortnummer
                  </label>
                  <input
                    type="text"
                    id="cardNumber"
                    placeholder="1234 5678 9012 3456"
                    value={cardDetails.number}
                    onChange={(e) => setCardDetails({...cardDetails, number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="cardName" className="block text-sm text-gray-600 mb-1">
                    Namn på kortet
                  </label>
                  <input
                    type="text"
                    id="cardName"
                    placeholder="Anders Andersson"
                    value={cardDetails.name}
                    onChange={(e) => setCardDetails({...cardDetails, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="expiry" className="block text-sm text-gray-600 mb-1">
                      Utgångsdatum
                    </label>
                    <input
                      type="text"
                      id="expiry"
                      placeholder="MM/ÅÅ"
                      value={cardDetails.expiry}
                      onChange={(e) => setCardDetails({...cardDetails, expiry: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="cvc" className="block text-sm text-gray-600 mb-1">
                      CVC
                    </label>
                    <input
                      type="text"
                      id="cvc"
                      placeholder="123"
                      value={cardDetails.cvc}
                      onChange={(e) => setCardDetails({...cardDetails, cvc: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                      required
                    />
                  </div>
                </div>
                
                <button
                  type="submit"
                  disabled={isProcessing}
                  className={`w-full py-3 px-4 bg-[#A3C1DA] hover:bg-[#7A98B0] text-white rounded-lg font-medium 
                           mt-6 transition-colors ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isProcessing ? 'Bearbetar...' : `Betala ${selectedPlan === 'premium' ? '499 kr/mån' : selectedPlan === 'standard' ? '299 kr/mån' : '99 kr'}`}
                </button>
              </form>
            ) : paymentMethod === 'swish' ? (
              <form onSubmit={handleSwishSubmit} className="space-y-4">
                <div>
                  <label htmlFor="swishNumber" className="block text-sm text-gray-600 mb-1">
                    Ditt Swish-nummer
                  </label>
                  <div className="flex items-center">
                    <span className="bg-gray-100 px-3 py-2 border border-gray-300 border-r-0 rounded-l-md text-gray-500">
                      +46
                    </span>
                    <input
                      type="tel"
                      id="swishNumber"
                      placeholder="70 123 45 67"
                      value={swishNumber}
                      onChange={(e) => setSwishNumber(e.target.value)}
                      className="flex-grow px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-[#A3C1DA]"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Inget belopp kommer att dras förrän du godkänner betalningen i Swish-appen
                  </p>
                </div>
                
                <button
                  type="submit"
                  disabled={isProcessing}
                  className={`w-full py-3 px-4 bg-[#A3C1DA] hover:bg-[#7A98B0] text-white rounded-lg font-medium
                           mt-6 transition-colors ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isProcessing ? 'Skickar till Swish...' : 'Fortsätt till Swish'}
                </button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <CreditCard className="w-6 h-6 text-[#FFB3C7]" />
                    <h4 className="font-medium">Klarna</h4>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Betala med Klarna och få möjlighet att dela upp betalningen eller betala senare.
                  </p>
                  <ul className="text-sm space-y-1 text-gray-600">
                    <li className="flex items-center gap-2">
                      <div className="w-4 h-4 text-green-500 flex items-center justify-center">✓</div>
                      <span>Betala direkt</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-4 h-4 text-green-500 flex items-center justify-center">✓</div>
                      <span>Betala inom 14 dagar</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <div className="w-4 h-4 text-green-500 flex items-center justify-center">✓</div>
                      <span>Dela upp betalningen</span>
                    </li>
                  </ul>
                </div>
                
                <button
                  onClick={() => {
                    setIsProcessing(true);
                    setTimeout(() => {
                      navigate('/payment-confirmation', { state: { paymentMethod: 'klarna', plan: selectedPlan } });
                    }, 1500);
                  }}
                  disabled={isProcessing}
                  className={`w-full py-3 px-4 bg-[#FFB3C7] hover:bg-[#FF9EB8] text-white rounded-lg font-medium
                           transition-colors ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isProcessing ? 'Bearbetar...' : 'Fortsätt till Klarna'}
                </button>
              </div>
            )}
            
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Genom att fortsätta godkänner du våra <a href="#" className="text-[#A3C1DA]">användarvillkor</a> och <a href="#" className="text-[#A3C1DA]">integritetspolicy</a>
              </p>
            </div>
          </div>
        </div>
        
        {/* Bottom row: Pricing tiers */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">Priser</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Day Pass Plan */}
            <div
              className={`rounded-lg p-6 cursor-pointer transition-all flex flex-col h-[500px] ${
                selectedPlan === 'day'
                  ? 'bg-[#E6F0FA] shadow-md'
                  : 'bg-[#E6F0FA]/70 hover:bg-[#E6F0FA]'
              }`}
              onClick={() => setSelectedPlan('day')}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold text-black">Dagspass</span>
                <span className="text-2xl font-bold text-black">99 kr</span>
              </div>
              <p className="text-black text-sm">Perfekt när du bara behöver verktygen för en dag.</p>
              <p className="text-black text-sm mt-1">Spara tid, hitta rätt och arbeta med många ansökningar samtidigt.</p>
              
              <div className="mt-4 bg-gray-100 p-4 rounded-lg">
                <h3 className="font-semibold text-black mb-2">Detta ingår:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-gray-200 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">Avancerad sökning i 24 timmar</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-gray-200 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">Djupanalys (90 stiftelser)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-gray-200 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">Tillgång till e-post & webbsideinformation</span>
                  </li>
                </ul>
              </div>
              <div className="mt-auto pt-4 flex justify-center">
                <button
                  onClick={() => {
                    setSelectedPlan('day');
                    document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-[#A3C1DA] text-white py-2 rounded-lg font-medium hover:bg-[#7A98B0] transition-colors"
                >
                  Välj Dagspass
                </button>
              </div>
              <p className="mt-2 text-xs text-center text-black">Gäller i 24 timmar</p>
            </div>
            
            {/* Standard Plan */}
            <div
              className={`rounded-lg p-6 relative cursor-pointer transition-all flex flex-col h-[500px] ${
                selectedPlan === 'standard'
                  ? 'bg-[#A3C1DA] shadow-md'
                  : 'bg-[#A3C1DA]/70 hover:bg-[#A3C1DA]'
              }`}
              onClick={() => setSelectedPlan('standard')}
            >
              <div className="absolute top-0 right-0 bg-[#F2C1D1] text-white text-xs font-semibold px-2 py-1">
                Populärast
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold text-black">Standard</span>
                <span className="text-2xl font-bold text-black">299 kr/mån</span>
              </div>
              <p className="text-black text-sm">Avsluta när du vill. Ingen bindningstid.</p>
              <p className="text-black text-sm mt-1">Perfekt för dig som söker stiftelsemedel regelbundet.</p>
              <div className="mt-4 bg-white/10 p-4 rounded-lg">
                <h3 className="font-semibold text-black mb-2">Detta ingår:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-white/20 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">AI-driven matchning</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-white/20 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">Djupanalys (300 stiftelser/månad)</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-white/20 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">E-post & webbinfo</span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-auto pt-4 flex justify-center">
                <button
                  onClick={() => {
                    setSelectedPlan('standard');
                    document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-white text-[#A3C1DA] py-2 rounded-lg font-medium hover:bg-white/90 transition-colors"
                >
                  Välj Standard
                </button>
              </div>
              <p className="mt-2 text-xs text-center text-black">Avbryt när som helst</p>
            </div>
            
            {/* Premium Plan */}
            <div
              className={`rounded-lg p-6 cursor-pointer transition-all flex flex-col h-[500px] ${
                selectedPlan === 'premium'
                  ? 'bg-[#F2C1D1] shadow-md'
                  : 'bg-[#F2C1D1]/70 hover:bg-[#F2C1D1]'
              }`}
              onClick={() => setSelectedPlan('premium')}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold text-black">Premium</span>
                <span className="text-2xl font-bold text-black">499 kr/mån</span>
              </div>
              <p className="text-black text-sm">Avsluta när du vill. Ingen bindningstid.</p>
              <p className="text-black text-sm mt-1">Ökar dina chanser för att lyckas med dina ansökningar väsentligt.</p>
              
              <div className="mt-4 bg-white/10 p-4 rounded-lg">
                <h3 className="font-semibold text-black mb-2">Detta ingår:</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-white/20 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">Allt i Standard-paketet</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-white/20 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">Obegränsad djupanalys</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-5 h-5 mr-2 mt-0.5 bg-white/20 rounded-full flex-shrink-0 flex items-center justify-center text-black">✓</div>
                    <span className="text-sm text-black">Ansökningshjälpen AI</span>
                  </li>
                </ul>
              </div>
              
              <div className="mt-auto pt-4 flex justify-center">
                <button
                  onClick={() => {
                    setSelectedPlan('premium');
                    document.getElementById('payment-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="w-full bg-white text-[#F2C1D1] py-2 rounded-lg font-medium hover:bg-white/90 transition-colors"
                >
                  Välj Premium
                </button>
              </div>
              <p className="mt-2 text-xs text-center text-black">Avbryt när som helst</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
